import { deserializeS } from '../../place'
import { TransportMode, Waypoint, WaypointType } from '../waypoints'

export interface DirectionsData {
  mode: TransportMode
  waypoints: Waypoint[]
}

export interface CreateWaypointsPathOptions {
  ignoreEmpty?: boolean
}

type FullWaypoint = Required<Waypoint>
export enum Avoid {
  tunnel = 'tunnel',
  motorway = 'motorway',
  boatFerry = 'boatFerry',
  tollroad = 'tollroad',
  dirtRoad = 'dirtRoad',
  railFerry = 'railFerry',
}

export enum MapType {
  normal = 'normal',
  satellite = 'satellite',
  terrain = 'terrain',
  hybrid = 'hybrid',
  traffic = 'traffic',
  pt = 'pt',
  panorama = 'panorama',
}

type FullWayoint = Required<Waypoint>
type LatLngWaypoint = Required<Pick<Waypoint, 'lat' | 'lng' | 'type'>>

enum Keyword {
  myLocation = 'mylocation',
  yourLocation = 'Your Location',
}

enum Index {
  transportMode = 0,
  waypoint = 1,
}

enum Prefix {
  waypoint = ':',
}

interface DecodedPlace {
  address?: string
  name?: string
  latitude?: number
  longitude?: number
  providerId?: number
  providerName?: string
}

/**
 * A function that gets the waypoints and the transport mode and creates the deeplink path for the directions
 *
 * @param waypoints The waypoints to create the path from
 * @param transportMode The transport mode to use for the directions path as search param
 * @param guidanceMode The guidance mode to change the main path from r to g
 * @param avoid The avoid options to use for the directions path as search param
 * @param zoom The zoom level to use for the directions path as search param
 * @param mapType The map type to use for the directions path as search param
 * @returns the url path with the waypoints and the search params
 */
export function createDeepLink(
  waypoints: Waypoint[],
  transportMode = TransportMode.default,
  guidanceMode = false,
  avoid?: Avoid[],
  zoom?: number,
  mapType?: MapType
): string {
  const guidancePath = guidanceMode ? 'g' : 'r'
  const url = new URL(`https://share.here.com/${guidancePath}/`)
  const isZoomValid = zoom !== undefined && zoom >= 0 && zoom <= 20

  waypoints
    .filter(
      (waypoint) => waypoint.type !== WaypointType.empty && waypoint.type !== WaypointType.any
    )
    .forEach((waypoint, index) => {
      const waypointPathSeparator = index === waypoints.length - 1 ? '' : '/'
      switch (waypoint.type) {
        case WaypointType.full:
          url.pathname += `${(waypoint as FullWayoint).lat},${(waypoint as FullWayoint).lng},${
            (waypoint as FullWayoint).name
          }${waypointPathSeparator}`
          break
        case WaypointType.latLng:
          url.pathname += `${(waypoint as LatLngWaypoint).lat},${
            (waypoint as LatLngWaypoint).lng
          }${waypointPathSeparator}`
          break
        case WaypointType.myLocation:
          url.pathname += `${Keyword.myLocation}${waypointPathSeparator}`
          break
        default:
          url.pathname = String(url.pathname)
      }
    })

  avoid && url.searchParams.set('a', avoid.join(','))
  transportMode && url.searchParams.set('m', __getDeepLinkTransportModeInitials(transportMode))
  isZoomValid && url.searchParams.set('z', String(zoom))
  mapType && url.searchParams.set('t', mapType)

  return url.toString()
}

/**
 * A helper function to get the supported transport mode initials for the deeplink path
 *
 * @param transportMode The transport mode to get the initials for
 * @returns The transport mode initials string
 */
export function __getDeepLinkTransportModeInitials(transportMode: TransportMode): string {
  switch (transportMode) {
    case TransportMode.drive:
      return 'd'
    case TransportMode.walk:
      return 'w'
    case TransportMode.bicycle:
      return 'b'
    case TransportMode.publicTransport:
      return 'pt'
    default:
      return 'd'
  }
}

/**
 * A function that gets Waypoints and or the transport mode, and creates the directions path
 *
 * @param waypoints An array of Waypoint instances include the waypoints title lat, lng, and Waypoint type
 * @param transportMode The transport mode to use for the directions path
 * @returns the waypoints in a comma separated format, and slashes between the different waypoint and or transport mode.
 */
export function createDirectionsPath(
  waypoints: Waypoint[],
  transportMode = TransportMode.default
): string {
  let mode = transportMode
  if (!__isTransportModeValid(mode)) {
    mode = TransportMode.default
  }
  return waypoints.reduce((acc, waypoint, index) => {
    let link = ''
    switch (waypoint.type) {
      case WaypointType.full:
        link = __createFullWaypointPath(waypoint as FullWaypoint)
        break
      case WaypointType.latLng:
        link = __createLatLngWaypointPath(waypoint as LatLngWaypoint)
        break
      case WaypointType.myLocation:
        link = __createMyLocationWaypointPath()
        break
      default:
        link = __createEmptyWaypointPath()
    }

    const waypointPathSeparator = index === waypoints.length - 1 ? '' : '/'
    return link ? `${acc}${link}${waypointPathSeparator}` : acc + '/'
  }, `${mode}/`)
}

export function __isTransportModeValid(transportMode: TransportMode): boolean {
  return Boolean(Object.values(TransportMode).find((value) => transportMode === value))
}
/**
 * Private function that creates the full waypoint path
 *
 * @param waypoint The waypoint object with all the properties set to required
 * @returns The full waypoint path, in format 'lat,lng,name'
 */
export function __createFullWaypointPath(waypoint: FullWaypoint): string {
  return `${waypoint.name}${Prefix.waypoint}${waypoint.lat},${waypoint.lng}`
}

/**
 * Private function that creates the lat,lng format waypoint path
 *
 * @param waypoint The waypoint object with all the required properties
 * @returns A 'lat,lng' format waypoint path
 */
export function __createLatLngWaypointPath(waypoint: LatLngWaypoint): string {
  return `${Prefix.waypoint}${waypoint.lat},${waypoint.lng}`
}

/**
 * Private function that create the keyword mylocation as part of the waypoint path
 *
 * @returns Teh keyword mylocation as part of the waypoint path
 */
export function __createMyLocationWaypointPath(): string {
  return Keyword.myLocation
}

/**
 * Private function that returns and empty waypoint path
 *
 * @returns empty string or a space character if emptyReturnValue is not set or is set to false.
 */
export function __createEmptyWaypointPath(): string {
  return ''
}

/**
 * A private function that splits and returns the path values that represent the waypoints
 *
 * @param path the directions path value
 * @returns The string values that represent the waypoints inside the path
 */
export function __getWaypointsFromPath(path: string): string[] {
  const pathValues = path.trim().replace(/\/$/, '').split('/')

  // ensures that if the transport mode is not set in the path at all, its default placeholder is set in pathValues array.
  const isTransportModeInPath =
    pathValues.length > 0 &&
    pathValues[0] !== TransportMode.default &&
    __getTransportModeFromPath(pathValues[0]) === TransportMode.default
  if (isTransportModeInPath) {
    pathValues.unshift(TransportMode.default)
  }

  return pathValues.slice(Index.waypoint, pathValues.length)
}

/**
 * A private function that creates the TransportMode value base on the path input value
 * @param path the directions path value
 * @returns The TransportMode enum that represents the string value.
 */
export function __getTransportModeFromPath(path: string): TransportMode {
  const pathValues = path.trim().replace(/\/$/, '').split('/')

  return __toTransportMode(pathValues[Index.transportMode])
}

/**
 * A private function that maps the transport mode string value comes from the path into TransportMode enum.
 *
 * @param value the transport mode value string
 * @returns The TransportMode enum
 */
export function __toTransportMode(value: string): TransportMode {
  switch (value) {
    case 'walk':
    case 'drive':
    case 'publicTransport':
    case 'bicycle':
      return TransportMode[value]
    default:
      return TransportMode.default
  }
}

/**
 * A function that gets the directions path and creates the DirectionData instance, including the waypoint and the TransportMode
 *
 * @param directionsPath is the direction path includes the TransportMode value and the waypoints
 * @returns DirectionData instance
 */
export function getDirectionsData(directionsPath: string): DirectionsData {
  const path =
    directionsPath.length > 0 && directionsPath[0] === '/'
      ? directionsPath.slice(1)
      : directionsPath
  const transportMode = __getTransportModeFromPath(path)

  return {
    mode: transportMode,
    waypoints: getWaypoints(path),
  }
}

/**
 * A function that gets the waypoints path and creates a list of Waypoint instances
 *
 * @param path is the waypoint path includes waypoint string value separated by '/'
 * @returns a list of Waypoint instances
 */
export function getWaypoints(path: string): Waypoint[] {
  if (path !== '') {
    const waypointPathValues = __getWaypointsFromPath(path)
    return waypointPathValues.map((waypoint: string): Waypoint => {
      const waypointType = __getWaypointType(waypoint)
      switch (waypointType) {
        case WaypointType.full:
          return __getFullWaypoint(waypoint)
        case WaypointType.latLng:
          return __getLatLngWaypoint(waypoint)
        case WaypointType.myLocation:
          return __getMyLocationWaypoint()
        case WaypointType.serializedE:
          return __getDeserializedEWaypoint(waypoint)
        case WaypointType.serializedS:
          return __getDeserializedSWaypoint(waypoint)
        default:
          return __getEmptyWaypoint()
      }
    })
  }

  return []
}

/**
 * A private function that creates Waypoint instance of type empty
 *
 * @returns a Waypoint instance of type 'empty'
 */
export function __getEmptyWaypoint(): Waypoint {
  return { type: WaypointType.empty }
}

/**
 * A private function that create a Waypoint instance of type myLocation
 *
 * @returns a Waypoint instance of type 'myLocation'
 */
export function __getMyLocationWaypoint(): Waypoint {
  return {
    type: WaypointType.myLocation,
  }
}

/**
 * A private function that creates a waypoint instance of type latLng
 * @param waypoint a waypoint path value of 'lat,lng' format
 * @returns A Waypoint instance
 */
export function __getLatLngWaypoint(waypoint: string): LatLngWaypoint {
  const rawValues = waypoint.split(',')
  const lat = Number(rawValues[0].slice(1))
  const lng = Number(rawValues[1])

  return {
    lat,
    lng,
    type: WaypointType.latLng,
  }
}

/**
 * A private function that created a FullWaypoint instance
 * @param waypoint a waypoint path value of 'name:lat,lng' format
 * @returns a FullWaypoint instance
 */
export function __getFullWaypoint(waypoint: string): FullWaypoint {
  const rawValues = waypoint.split(Prefix.waypoint)
  const name = decodeURIComponent(rawValues[0])
  const [lat, lng] = rawValues[1].split(',')

  return {
    lat: Number(lat),
    lng: Number(lng),
    name: decodeURIComponent(name),
    type: WaypointType.full,
  }
}

/**
 * This private function is responsible to recognize and deserialize any serialized value that represents a waypoint inside directions path.
 *
 * If the deserialization format fails the waypoint returned is of type empty.
 *
 * @param waypoint The string representation of the waypoint
 * @returns The Waypoint object
 */
export function __getDeserializedEWaypoint(waypoint: string): Waypoint {
  let decodedPlace
  try {
    decodedPlace = JSON.parse(window.atob(waypoint.replace(/e-/, ''))) as DecodedPlace
  } catch (err) {
    console.error('Error: failed to deserialize the value, defaults to empty')

    return __getEmptyWaypoint()
  }

  if (decodedPlace.latitude === undefined || decodedPlace.longitude === undefined) {
    console.warn('Warning: failed to get latitude or longitude for encoded value')

    return __getEmptyWaypoint()
  }

  const name =
    decodedPlace.name !== undefined
      ? decodedPlace.name
      : decodedPlace.address !== undefined
      ? decodedPlace.address
      : ''

  return { lat: decodedPlace.latitude, lng: decodedPlace.longitude, name, type: WaypointType.full }
}

/**
 * This private function is responsible to recognize and deserialize any serialized value that represents a waypoint inside directions path.
 *
 * If the deserialization format fails the waypoint returned is of type empty.
 *
 * @param waypoint The string representation of the waypoint
 * @returns The Waypoint object
 */
export function __getDeserializedSWaypoint(waypoint: string): Waypoint {
  let decodedPlace
  try {
    decodedPlace = deserializeS(waypoint)
  } catch (err) {
    console.error('Error: failed to deserialize the value, defaults to empty')

    return __getEmptyWaypoint()
  }

  if (decodedPlace.lat === undefined || decodedPlace.lon === undefined) {
    console.warn('Warning: failed to get latitude or longitude for encoded value')

    return __getEmptyWaypoint()
  }

  const name = decodedPlace.n !== undefined ? decodeURIComponent(decodedPlace.n) : ''

  return { lat: decodedPlace.lat, lng: decodedPlace.lon, name, type: WaypointType.full }
}

/**
 * This helper function is used to determine if the format of the waypoint is one of the accepted types.
 *
 * Note: the order of the condition check is set so that no logical overrides occur.
 *
 * The format can be serialized with e- prefix, lat,lng,name, lat,lng, empty string, or 'myLocation/yourLocation' reserved word.
 * @param waypoint A waypoint value part of the url path
 * @returns WaypointFormat type that represent that waypoint type based on the format
 */
export function __getWaypointType(waypoint: string): WaypointType {
  const isEmpty = waypoint.length === 0

  if (__isSerializedE(waypoint)) {
    return WaypointType.serializedE
  }

  if (__isSerializedS(waypoint)) {
    return WaypointType.serializedS
  }

  if (__isMyLocation(waypoint)) {
    return WaypointType.myLocation
  }

  if (__isLatLng(waypoint)) {
    return WaypointType.latLng
  }

  if (__isFull(waypoint)) {
    return WaypointType.full
  }

  if (isEmpty) {
    return WaypointType.empty
  }

  return WaypointType.any
}

function __isLatLng(waypoint: string): boolean {
  const latLngRgx = /^:-*\d+(\.\d){0,1}\d*,-*\d+(\.\d){0,1}\d*$/gi

  return latLngRgx.test(waypoint)
}

function __isFull(waypoint: string): boolean {
  const fullRgx = /^.+:-*\d+(\.\d){0,1}\d*,-*\d+(\.\d){0,1}\d*$/gi

  return fullRgx.test(waypoint)
}

function __isMyLocation(waypoint: string): boolean {
  const isMyLocationRgx = new RegExp(
    `${Keyword.myLocation}|${Keyword.yourLocation}|${encodeURIComponent(Keyword.yourLocation)}`,
    'gi'
  )

  return isMyLocationRgx.test(waypoint)
}

function __isSerializedE(waypoint: string): boolean {
  return waypoint.startsWith('e-')
}

function __isSerializedS(waypoint: string): boolean {
  return waypoint.startsWith('s-')
}
